import Template from "./template";
import paper from "paper";
import ComponentPort from "../core/componentPort";
import { LogicalLayerType } from "../core/init";
import { DEFAULT_CHANNEL_WIDTH_UM } from "./channelWidths";
import { resolveBinaryLeafCount } from "./branchLeafs";

export default class Tree extends Template {
    constructor() {
        super();
    }

    __setupDefinitions(): void  {
        this.__unique = {
            position: "Point"
        };

        this.__heritable = {
            componentSpacing: "Float",
            flowChannelWidth: "Float",
            rotation: "Float",
            leafSpace: "Float",
            in: "Integer",
            out: "Integer",
            depth: "Float",
            stageSpace: "Float",
            mirrorByX: "Float",
            mirrorByY: "Float"
        };

        this.__defaults = {
            componentSpacing: 2000,
            flowChannelWidth: DEFAULT_CHANNEL_WIDTH_UM,
            rotation: 0,
            leafSpace: 4 * 1000,
            in: 1,
            out: 8,
            depth: 250,
            stageSpace: 4000,
            mirrorByX: 0,
            mirrorByY: 0
        };

        this.__units = {
            componentSpacing: "μm",
            flowChannelWidth: "μm",
            rotation: "°",
            leafSpace: "μm",
            in: "",
            out: "",
            depth: "μm",
            stageSpace: "μm"
        };

        this.__minimum = {
            componentSpacing: 0,
            flowChannelWidth: 10,
            leafSpace: 30,
            in: 1,
            out: 2,
            depth: 10,
            stageSpace: 100,
            rotation: 0,
            mirrorByX: 0,
            mirrorByY: 0
        };

        this.__maximum = {
            componentSpacing: 10000,
            flowChannelWidth: 2000,
            leafSpace: 12000,
            in: 1,
            out: 128,
            depth: 1200,
            stageSpace: 6000,
            rotation: 360,
            mirrorByX: 1,
            mirrorByY: 1
        };

        this.__featureParams = {
            componentSpacing: "componentSpacing",
            position: "position",
            flowChannelWidth: "flowChannelWidth",
            rotation: "rotation",
            leafSpace: "leafSpace",
            in: "in",
            out: "out",
            stageSpace: "stageSpace",
            mirrorByX: "mirrorByX",
            mirrorByY: "mirrorByY"
        };

        this.__targetParams = {
            componentSpacing: "componentSpacing",
            flowChannelWidth: "flowChannelWidth",
            rotation: "rotation",
            leafSpace: "leafSpace",
            in: "in",
            out: "out",
            stageSpace: "stageSpace",
            mirrorByX: "mirrorByX",
            mirrorByY: "mirrorByY"
        };

        this.__placementTool = "componentPositionTool";

        this.__toolParams = {
            position: "position"
        };

        this.__renderKeys = ["FLOW"];

        this.__mint = "TREE";

        this.__zOffsetKeys = {
            FLOW: "depth"
        };

        this.__substrateOffset = {
            FLOW: "0"
        };
    }

    __treeLeafSpace(params: { [k: string]: any }): number {
        const named = Number(params.leafSpace);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const legacy = Number(params.spacing);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 4000;
    }

    __treeStageSpace(params: { [k: string]: any }): number {
        const named = Number(params.stageSpace);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const legacy = Number(params.stageLength);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 4000;
    }

    getPorts(params: { [k: string]: any }) {
        const ports = [];
        const cw = params.flowChannelWidth;
        const spacing = this.__treeLeafSpace(params);
        // out must be ≥ 2 (same rule as MUX); pad non-powers of 2 upward.
        const leafs = Math.max(2, resolveBinaryLeafCount(params).leafs);
        const stagelength = this.__treeStageSpace(params);

        const levels = Math.log2(leafs);
        const w = spacing * (leafs / 2 + 1);

        const length = levels * (cw + stagelength) + stagelength;
        const width = 2 * 0.5 * w * 2 * Math.pow(0.5, levels);

        ports.push(new ComponentPort(0, 0, "1", LogicalLayerType.FLOW));

        for (let i = 0; i < leafs; i++) {
            ports.push(new ComponentPort(((leafs - 1) * width) / 2 - i * width, length, (2 + i).toString(), LogicalLayerType.FLOW));
        }

        return ports;
    }

    render2D(params: { [k: string]: any }, key: string): paper.CompoundPath  {
        const position = params.position;
        const cw = params.flowChannelWidth;
        const spacing = this.__treeLeafSpace(params);
        const leafs = Math.max(2, resolveBinaryLeafCount(params).leafs);
        const color = params.color;
        const stagelength = this.__treeStageSpace(params);
        const px = position[0];
        const py = position[1];

        const treepath = new paper.CompoundPath("");
        const levels = Math.log2(leafs);
        const w = spacing * (leafs / 2 + 1);
        this.__generateTwig(treepath, px, py, cw, stagelength, w, 1, levels);

        treepath.fillColor = color;
        this.transformRender(params, treepath);
        return treepath;
    }

    __generateTwig(treepath: paper.CompoundPath, px: number, py: number, cw: number, stagelength: number, newspacing: number, level: number, maxlevel: number, islast = false): void  {
        // var newspacing = 2 * (spacing + cw);
        const hspacing = newspacing / 2;
        const lex = px - 0.5 * newspacing;
        const ley = py + cw + stagelength;
        const rex = px + 0.5 * newspacing;
        const rey = py + cw + stagelength;

        if (level === maxlevel) {
            islast = true;
            // console.log("Final Spacing: " + newspacing)
        }

        this.__drawtwig(treepath, px, py, cw, stagelength, newspacing, islast);
        // drawtwig(treepath, lex, ley, cw, stagelength, hspacing, islast);
        // drawtwig(treepath, rex, rey, cw, stagelength, hspacing, islast);

        if (!islast) {
            this.__generateTwig(treepath, lex, ley, cw, stagelength, hspacing, level + 1, maxlevel);
            this.__generateTwig(treepath, rex, rey, cw, stagelength, hspacing, level + 1, maxlevel);
        }
    }

    __drawtwig(treepath: paper.CompoundPath, px: number, py: number, cw: number, stagelength: number, spacing: number, drawleafs = false): paper.CompoundPath  {
        // stem
        let startPoint = new paper.Point(px - cw / 2, py);
        let endPoint = new paper.Point(px + cw / 2, py + stagelength);
        let rec = new paper.Path.Rectangle({
            from: startPoint,
            to: endPoint,
            radius: 0,
            strokeWidth: 0
        });

        treepath.addChild(rec);

        // Draw 2 leafs
        // left leaf
        const lstartx = px - 0.5 * (cw + spacing);
        const lendx = lstartx + cw;
        const lstarty = py + stagelength + cw;
        const lendy = lstarty + stagelength;

        // //right leaf
        const rstartx = px + 0.5 * (spacing - cw);
        const rendx = rstartx + cw;
        const rstarty = py + stagelength + cw;
        const rendy = rstarty + stagelength;

        if (drawleafs) {
            startPoint = new paper.Point(lstartx, lstarty);
            endPoint = new paper.Point(lendx, lendy);
            rec = new paper.Path.Rectangle({
                from: startPoint,
                to: endPoint,
                radius: 0,
                strokeWidth: 0
            });
            treepath.addChild(rec);

            startPoint = new paper.Point(rstartx, rstarty);
            endPoint = new paper.Point(rendx, rendy);
            rec = new paper.Path.Rectangle({
                from: startPoint,
                to: endPoint,
                radius: 0,
                strokeWidth: 0
            });
            treepath.addChild(rec);
        }

        // Horizontal bar
        const hstartx = px - 0.5 * (cw + spacing);
        const hendx = rendx;
        const hstarty = py + stagelength;
        const hendy = hstarty + cw;
        startPoint = new paper.Point(hstartx, hstarty);
        endPoint = new paper.Point(hendx, hendy);
        rec = new paper.Path.Rectangle({
            from: startPoint,
            to: endPoint,
            radius: 0,
            strokeWidth: 0
        });
        treepath.addChild(rec);
        return treepath;
    }

    render2DTarget(key: string | null, params: { [k: string]: any }): paper.CompoundPath  {
        if (key === null) {
            key = this.__renderKeys[0];
        }
        const render = this.render2D(params, key);
        render.fillColor!.alpha = 0.5;
        return render;
    }
}
